import * as Sentry from '@sentry/react';
import { handeleCanvasCode, handeleCanvasUserClasses, handeleCanvasUserLogin, handleCheckUserLti, handleCheckUserAuthOnLti } from "store/canvas/thunks";
import { handleLoginWithToken } from 'store/auth/thunks';
import { has, get } from 'lodash';

const local_store_keys = {
    canvas_selected_district: "canvas_selected_district",
    canvas_auth_token: "canvas_auth_token",
    canvas_user_classes_cache: "canvas_user_classes_cache"
}

const CACHE_DURATION_MS = 3 * 60 * 60 * 1000;

const getCacheKey = (canvas_id, canvas_auth_id) => {
    return `${local_store_keys.canvas_user_classes_cache}_${canvas_id}_${canvas_auth_id}`;
}

const getCachedUserClasses = (canvas_id, canvas_auth_id) => {
    try {
        const cacheKey = getCacheKey(canvas_id, canvas_auth_id);
        const cachedData = localStorage.getItem(cacheKey);
        if (!cachedData) {
            return null;
        }
        const { timestamp, data } = JSON.parse(cachedData);
        const now = Date.now();
        const age = now - timestamp;
        if (age < CACHE_DURATION_MS) {
            return data;
        }
        localStorage.removeItem(cacheKey);
        return null;
    } catch (error) {
        console.error('Error reading cached user classes:', error);
        return null;
    }
}

const setCachedUserClasses = (canvas_id, canvas_auth_id, data) => {
    try {
        const cacheKey = getCacheKey(canvas_id, canvas_auth_id);
        const cacheData = {
            timestamp: Date.now(),
            data: data
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
        console.error('Error caching user classes:', error);
    }
}

const shouldSyncUserClasses = (canvas_id, canvas_auth_id) => {
    const cached = getCachedUserClasses(canvas_id, canvas_auth_id);
    return cached === null;
}

export const handleCanvasAuth = async ({
    search,
    setIsAutoLogin,
    setError,
    setAutologinMessage,
    setLoading,
    loginWithSchoology,
    dispatch
}) => {
    setLoading(true);
    setIsAutoLogin(true);
    
    try {
        if (typeof search === "object" && search.code) {
            if(typeof search.code !== "string") {
                setAutologinMessage("Invalid code. Please try again or contact support for assistance.");
                setError("Invalid code. Please try again or contact support for assistance.");
                setLoading(false);
                setIsAutoLogin(false);
                Sentry.captureException(`Invalid code. Please try again or contact support for assistance. `);
                return;
            }
            const canvas_selected_auth_id = localStorage.getItem(local_store_keys.canvas_selected_district);
            const deepLinkIdToken = sessionStorage.getItem('lti_deep_link_id_token');
            const deepLinkState = sessionStorage.getItem('lti_deep_link_state');
            
            let domain = null;
            if (document.referrer) {
                domain = new URL(document.referrer).hostname;
            }
            
            if ((domain && !domain.includes("clever.com")) || canvas_selected_auth_id) {
                const canvasAuth = await handeleCanvasCode(search.code, canvas_selected_auth_id, domain);
                    if (canvasAuth && canvasAuth.access_token) {
                    localStorage.removeItem(local_store_keys.canvas_selected_district);
                    if (deepLinkIdToken) {
                        sessionStorage.removeItem('lti_deep_link_id_token');
                        sessionStorage.removeItem('lti_deep_link_state');
                        await handleCanvas(canvasAuth, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch, isDeepLinking: true, id_token: deepLinkIdToken, state: deepLinkState });
                    } else {
                        await handleCanvas(canvasAuth, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch });
                    }
                    return;
                }
                return ({
                    not_a_canvas_auth: true,
                })
            } else {
                return ({
                    not_a_canvas_auth: true,
                })
            }
            
            setAutologinMessage('Something went wrong. Please try again later.');
            setError('Something went wrong. Please try again later.');
            setLoading(false);
            setIsAutoLogin(false);
        }
        else if (typeof search === "object" && search.custom_canvas_api_domain) {
            const { custom_canvas_api_domain, custom_canvas_user_id } = search;
            const canv_user = await handleCheckUserLti(custom_canvas_user_id, custom_canvas_api_domain);
            if (canv_user && canv_user.success) {
                const udata = canv_user.data;
                if (udata && udata.auth_found) {
                    await handleCanvas(canv_user.data, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch });
                } else if (udata.auth) {
                    const auth_data = udata.auth;
                    if (auth_data && auth_data.base_url) {
                        localStorage.setItem(local_store_keys.canvas_selected_district, auth_data.id);
                        authenticateWithCanvas(auth_data.base_url, auth_data.key, auth_data.redirect_uri);
                    } else {
                        setAutologinMessage(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                        setError(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                        setLoading(false);
                        setIsAutoLogin(false);
                    }
                } else {
                    setAutologinMessage('Your district is not configured. Please contact the system administrator.');
                    setError('Your district is not configured. Please contact the system administrator.');
                    setLoading(false);
                    setIsAutoLogin(false);
                }
            } else {
                setAutologinMessage('Something went wrong. Please try again later.');
                setError('Something went wrong. Please try again later.');
                setLoading(false);
                setIsAutoLogin(false);
            }
        }
        else if (typeof search === "object" && search.lti_message_type) {
            if (document.referrer && search.user_id) {
                const domain = new URL(document.referrer).hostname;
                const canv_user = await handleCheckUserLti(search.user_id, domain);
                if (canv_user && canv_user.success) {
                    const udata = canv_user.data;
                    if (udata && udata.auth_found) {
                        await handleCanvas(canv_user.data, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch });
                    } else if (udata.auth) {
                        const auth_data = udata.auth;
                        if (auth_data && auth_data.base_url) {
                            localStorage.setItem(local_store_keys.canvas_selected_district, auth_data.id);
                            authenticateWithCanvas(auth_data.base_url, auth_data.key, auth_data.redirect_uri);
                        } else {
                            setAutologinMessage(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                            setError(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                            setLoading(false);
                            setIsAutoLogin(false);
                        }
                    } else {
                        setAutologinMessage('Your district is not configured. Please contact the system administrator.');
                        setError('Your district is not configured. Please contact the system administrator.');
                        setLoading(false);
                        setIsAutoLogin(false);
                    }
                } else {
                    setAutologinMessage('Something went wrong. Please try again later.');
                    setError('Something went wrong. Please try again later.');
                    setLoading(false);
                    setIsAutoLogin(false);
                }
            } else {
                setAutologinMessage('Something went wrong. Please try again later.');
                setError('Something went wrong. Please try again later.');
                setLoading(false);
                setIsAutoLogin(false);
            }
        }
        else if (typeof search === "object" && search.id_token) {
            const { id_token, state } = search;
            
            const canv_user = await handleCheckUserAuthOnLti(id_token, document.referrer);
            if (canv_user && canv_user.success) {
                const udata = canv_user.data;
                const isDeepLinking = udata.isDeepLinking === true;
                
                if (isDeepLinking) {
                    sessionStorage.setItem('lti_deep_link_id_token', id_token);
                    sessionStorage.setItem('lti_deep_link_state', state || '');
                }
                
                if (udata && udata.auth_found) {
                    await handleCanvas(canv_user.data, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch, isDeepLinking, id_token, state, redirectData: udata.redirectData });
                } else if (udata.auth) {
                    const auth_data = udata.auth;
                    if (auth_data && auth_data.base_url) {
                        localStorage.setItem(local_store_keys.canvas_selected_district, auth_data.id);
                        authenticateWithCanvas(auth_data.base_url, auth_data.key, auth_data.redirect_uri);
                    } else {
                        setAutologinMessage(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                        setError(canv_user && canv_user.message ? canv_user.message : 'Something went wrong. Please try again later.');
                        setLoading(false);
                        setIsAutoLogin(false);
                    }
                } else {
                    setAutologinMessage('Your district is not configured. Please contact the system administrator.');
                    setError('Your district is not configured. Please contact the system administrator.');
                    setLoading(false);
                    setIsAutoLogin(false);
                }
            } else {
                setAutologinMessage('Something went wrong. Please try again later.');
                setError('Something went wrong. Please try again later.');
                setLoading(false);
                setIsAutoLogin(false);
            }
        }
    } catch (error) {
        Sentry.captureException(`591 Error in handleCanvasAuth: ${error}`);
        console.log(error, "errorerror");
        setAutologinMessage('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setError('There was an error while processing the auto-login. Please try again or contact support for assistance.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

const handleCanvas = async (authInfo, { setAutologinMessage, setError, setLoading, setIsAutoLogin, loginWithSchoology, dispatch, isDeepLinking, id_token, state, redirectData }) => {
    try {
        if (authInfo && authInfo.access_token) {
            localStorage.setItem(local_store_keys.canvas_auth_token, authInfo.access_token);
        }

        const doTokenLogin = async (token, userPayload) => {
            if (!dispatch || !token) return null;
            const result = await dispatch(handleLoginWithToken(token));
            if (!result || !result.isSignedIn) return null;
            return result;
        };

        const finishLogin = async (userPayload, redirectDataArg) => {
            try {
                if (has(userPayload, 'canvas_id') && shouldSyncUserClasses(userPayload.canvas_id, userPayload.canvas_auth_id || userPayload.auth_id)) {
                    const accessToken = authInfo?.access_token || userPayload.access_token;
                    if (accessToken) {
                        const result = await handeleCanvasUserClasses(accessToken, userPayload.canvas_id, userPayload.canvas_auth_id || userPayload.auth_id);
                        if (result) setCachedUserClasses(userPayload.canvas_id, userPayload.canvas_auth_id || userPayload.auth_id, result);
                    }
                }
            } catch (err) {
                Sentry.captureException(`594 Error in handleCanvas: ${err}`);
            }
            await loginWithSchoology(userPayload, null, redirectDataArg);
            if (isDeepLinking && id_token) {
                sessionStorage.setItem('lti_deep_link_id_token', id_token);
                sessionStorage.setItem('lti_deep_link_state', state || '');
            }
            setLoading(false);
            setIsAutoLogin(false);
        };

        if (authInfo?.token && dispatch) {
            const result = await doTokenLogin(authInfo.token, authInfo);
            if (result) {
                const userPayload = { ...authInfo, username: result.username };
                await finishLogin(userPayload, redirectData);
                return;
            }
            setAutologinMessage('Authentication failed. Please try login again!');
            setError('Authentication failed. Please try login again!');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        if (has(authInfo, 'username') && !authInfo.access_token) {
            setAutologinMessage('Unable to complete login. Please try again.');
            setError('Unable to complete login. Please try again.');
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        if (authInfo?.access_token && !has(authInfo, 'username')) {
            setAutologinMessage('Please wait while auto-login is in progress.');
            setError('Please wait while auto-login is in progress.');
            setLoading(true);
            setIsAutoLogin(true);

            const hasReg = await handeleCanvasUserLogin(authInfo.access_token, get(authInfo, 'user.global_id'), authInfo.auth_id, authInfo.user);
            setAutologinMessage('User data fetched successfully. Awaiting class synchronization…');

            if (has(hasReg, 'id') && hasReg.token && dispatch) {
                const result = await doTokenLogin(hasReg.token, hasReg);
                if (result) {
                    const userPayload = { ...hasReg, username: result.username, access_token: authInfo.access_token, auth_id: authInfo.auth_id };
                    await finishLogin(userPayload, redirectData);
                    return;
                }
            }

            if (has(hasReg, 'id')) {
                setAutologinMessage('Authentication failed. Please try login again!');
                setError('Authentication failed. Please try login again!');
                setLoading(false);
                setIsAutoLogin(false);
                return;
            }

            if (hasReg && typeof hasReg.error === 'string') {
                setAutologinMessage(hasReg.error);
                setError(hasReg.error);
            } else {
                setAutologinMessage('Something went wrong. Please try again later.');
                setError('Something went wrong. Please try again later.');
            }
            setLoading(false);
            setIsAutoLogin(false);
            return;
        }

        setAutologinMessage('Unable to fetch the user with the provided code. Please try again.');
        setError('Unable to fetch the user with the provided code. Please try again.');
        setLoading(false);
        setIsAutoLogin(false);
    } catch (error) {
        Sentry.captureException(`595 Error in handleCanvas: ${error}`);
        setAutologinMessage('Something went wrong. Please try again later.');
        setError('Something went wrong. Please try again later.');
        setLoading(false);
        setIsAutoLogin(false);
    }
};

export const authenticateWithCanvas = (baseUrl, clientId, redirectUri) => {
    let _redirect_uri = window.location.origin.includes("localhost") || !redirectUri ? window.location.origin : redirectUri;
    _redirect_uri = _redirect_uri.replace('www.', '');
    window.location.href = `${baseUrl}/login/oauth2/auth?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(_redirect_uri)}`;
};
